import React, { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Icons } from "../components/ui/icons";
import { RefreshToolbarButton } from "../components/refresh-toolbar-button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import StatisticsCard from "../components/statistics-card";
import DataTable from "../components/data-table";
import {
  recentQuotesTableColumns,
  recentInvoicesTableColumns,
  recentMovementsTableColumns,
} from "../lib/table-columns";
import {
  getDashboardOverview,
  getDashboardRecentActivity,
  getDashboardQuickMetrics,
  getDashboardMonthlyTrend,
  DashboardOverview,
  DashboardQuickMetrics,
  DashboardMonthlyTrend,
  DashboardActivity,
} from "../lib/api";
import { getBackendErrorMessage } from "../lib/utils";
import { TableRowData } from "../components/data-table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import DashboardLoadingState from "../components/dashboard-loading-state";
import { useIsMobile } from "../hooks/use-is-mobile";
import MobileDashboard from "./dashboard/MobileDashboard";

interface ActivityTableRow extends TableRowData {
  reference?: string;
  customer?: string;
  amount?: string;
  status?: string;
  date?: string;
}

const Dashboard: React.FC = () => {
  const isMobile = useIsMobile();
  return isMobile ? <MobileDashboard /> : <DesktopDashboard />;
};

const DesktopDashboard: React.FC = () => {
  const dashboardQuery = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => {
      const [overviewData, activityData, metricsData, trendData] =
        await Promise.all([
          getDashboardOverview(),
          getDashboardRecentActivity(),
          getDashboardQuickMetrics(),
          getDashboardMonthlyTrend(),
        ]);
      return {
        overview: overviewData as DashboardOverview,
        recentActivity: (activityData.activities || []) as DashboardActivity[],
        quickMetrics: metricsData as DashboardQuickMetrics,
        monthlyTrend: trendData as DashboardMonthlyTrend,
      };
    },
  });

  const overview = dashboardQuery.data?.overview ?? null;
  const recentActivity = dashboardQuery.data?.recentActivity ?? [];
  const quickMetrics = dashboardQuery.data?.quickMetrics ?? null;
  const monthlyTrend = dashboardQuery.data?.monthlyTrend ?? null;
  const loading = dashboardQuery.isLoading;
  const error = dashboardQuery.error
    ? getBackendErrorMessage(dashboardQuery.error, "Failed to load dashboard data")
    : null;

  // Transform recent activity to table data, filtering for invoices and quotations only
  const recentActivityTableData: ActivityTableRow[] = useMemo(
    () =>
      recentActivity
        .filter((activity) => activity.type === "invoice" || activity.type === "quotation")
        .slice(0, 5)
        .map((activity) => ({
          id: activity.id,
          reference: activity.reference || "-",
          customer: activity.customer || "-",
          site: activity.site || "-",
          amount: `${activity?.currencyType || "TZS"} ${activity.amount?.toLocaleString() || "-"}`,
          status: activity.status,
          date: new Date(activity.createdAt).toLocaleDateString(),
        })),
    [recentActivity],
  );

  // Transform recent activity to tools on rent data (movements)
  const recentMovementsData: ActivityTableRow[] = useMemo(
    () =>
      recentActivity
        .filter((activity) => activity.type === "movement")
        .slice(0, 5)
        .map((activity) => ({
          id: activity.id,
          activityType: activity.activityType || "-",
          activitySubType: activity.activitySubType || "-",
          source: activity.source || "-",
          destination: activity.destination || "-",
          status: activity.status,
          date: new Date(activity.createdAt).toLocaleDateString(),
        })),
    [recentActivity],
  );

  return (
    <>
      {loading ? (
        <DashboardLoadingState />
      ) : (
        <div className="h-full w-full bg-gray-50 overflow-hidden flex flex-col">
          <div className="flex-grow overflow-auto p-4 md:p-6 m-3 bg-background text-foreground border border-gray-100 rounded-xl">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <div>
                <h1 className="text-2xl font-bold font-jakarta">Dashboard</h1>
                <p className="text-gray-800 text-sm mt-1">
                  Easily track and manage your equipment rentals for every building
                  site.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <RefreshToolbarButton
                  isRefreshing={dashboardQuery.isFetching}
                  onRefresh={() => dashboardQuery.refetch()}
                />
              </div>
            </div>

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                {error}
              </div>
            )}

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[
                  {
                    label: "Total Customers",
                    value: (overview?.summary?.customers || 0).toLocaleString(),
                    icon: <Icons.Clients className="w-5 h-5 text-primary-600" />,
                  },
                  {
                    label: "Total Sites",
                    value: (overview?.summary?.sites || 0).toLocaleString(),
                    icon: <Icons.Location className="w-5 h-5 text-primary-600" />,
                  },
                  {
                    label: "Quotations",
                    value: (overview?.summary?.quotations || 0).toLocaleString(),
                    icon: <Icons.Quotes className="w-5 h-5 text-primary-600" />,
                  },
                  {
                    label: "Invoices",
                    value: (overview?.summary?.invoices || 0).toLocaleString(),
                    icon: <Icons.Billing className="w-5 h-5 text-primary-600" />,
                  },
                  {
                    label: "Warehouses",
                    value: (overview?.summary?.warehouses || 0).toLocaleString(),
                    icon: <Icons.Building className="w-5 h-5 text-primary-600" />,
                  },
                  {
                    label: "Movements",
                    value: (overview?.summary?.movements || 0).toLocaleString(),
                    icon: <Icons.Movement className="w-5 h-5 text-primary-600" />,
                  },
                  {
                    label: "Available Items",
                    value: (overview?.inventory?.totalAvailable || 0).toLocaleString(),
                    icon: <Icons.InStock className="w-5 h-5 text-primary-600" />,
                  },
                  {
                    label: "Rented Items",
                    value: (overview?.inventory?.totalRented || 0).toLocaleString(),
                    icon: <Icons.RentedOut className="w-5 h-5 text-primary-600" />,
                  },
                  {
                    label: "Reserved Items",
                    value: (overview?.inventory?.totalReserved || 0).toLocaleString(),
                    icon: <Icons.Reserved className="w-5 h-5 text-primary-600" />,
                  },
                  {
                    label: "Damaged Items",
                    value: (overview?.inventory?.totalDamaged || 0).toLocaleString(),
                    icon: <Icons.Damaged className="w-5 h-5 text-red-600" />,
                  },
                  {
                    label: "Lost Items",
                    value: (overview?.inventory?.totalLost || 0).toLocaleString(),
                    icon: <Icons.Lost className="w-5 h-5 text-gray-700" />,
                  },
                  {
                    label: "Total Items Value",
                    value: `TZS ${(overview?.inventory?.totalValue || 0).toLocaleString()}`,
                    icon: <Icons.Billing className="w-5 h-5 text-primary-600" />,
                  },
                ].map((item, index) => (
                  <StatisticsCard
                    key={index}
                    label={item.label}
                    value={item.value}
                    icon={item.icon}
                  />
                ))}
              </div>

              {/* Monthly Trends Chart */}
              {monthlyTrend &&
                monthlyTrend.trends &&
                monthlyTrend.trends.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">
                        Monthly Trends - Last {monthlyTrend.period} Months
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="w-full h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={monthlyTrend.trends}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis
                              dataKey="month"
                              tick={{ fontSize: 12 }}
                              stroke="#888"
                            />
                            <YAxis tick={{ fontSize: 12 }} stroke="#888" />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#fff",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                              }}
                            />
                            <Legend wrapperStyle={{ paddingTop: "20px" }} />
                            <Bar
                              dataKey="quotations.count"
                              fill="#8dd4ff"
                              name="Quotations"
                              radius={[4, 4, 0, 0]}
                            />
                            <Bar
                              dataKey="invoices.count"
                              fill="#d4b3ff"
                              name="Invoices"
                              radius={[4, 4, 0, 0]}
                            />
                            <Bar
                              dataKey="movements"
                              fill="#ffc580"
                              name="Movements"
                              radius={[4, 4, 0, 0]}
                            />
                            <Bar
                              dataKey="customers"
                              fill="#ff9999"
                              name="New Customers"
                              radius={[4, 4, 0, 0]}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

              {/* Revenue Trend */}
              {monthlyTrend &&
                monthlyTrend.trends &&
                monthlyTrend.trends.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Revenue Trend</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="w-full h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={monthlyTrend.trends}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis
                              dataKey="month"
                              tick={{ fontSize: 12 }}
                              stroke="#888"
                            />
                            <YAxis tick={{ fontSize: 12 }} stroke="#888" />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#fff",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                              }}
                              formatter={(value: any) =>
                                `TZS ${value?.toLocaleString() || 0}`
                              }
                            />
                            <Legend wrapperStyle={{ paddingTop: "20px" }} />
                            <Line
                              type="monotone"
                              dataKey="quotations.total"
                              stroke="#8dd4ff"
                              name="Quotations Total"
                              strokeWidth={2}
                              dot={{ fill: "#8dd4ff", r: 4 }}
                            />
                            <Line
                              type="monotone"
                              dataKey="invoices.total"
                              stroke="#d4b3ff"
                              name="Invoices Total"
                              strokeWidth={2}
                              dot={{ fill: "#d4b3ff", r: 4 }}
                            />
                            <Line
                              type="monotone"
                              dataKey="invoices.paid"
                              stroke="#ffc580"
                              name="Paid Invoices"
                              strokeWidth={2}
                              dot={{ fill: "#ffc580", r: 4 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

              {/* Data Tables */}
              {recentActivity.some((a) => a.type === "quotation") && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Recent Quotes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DataTable
                      columns={recentQuotesTableColumns}
                      data={recentActivityTableData.filter((a) =>
                        recentActivity.find(
                          (act) => act.id === a.id && act.type === "quotation",
                        ),
                      )}
                    />
                  </CardContent>
                </Card>
              )}

              {recentActivity.some((a) => a.type === "invoice") && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Recent Invoices</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DataTable
                      columns={recentInvoicesTableColumns}
                      data={recentActivityTableData.filter((a) =>
                        recentActivity.find(
                          (act) => act.id === a.id && act.type === "invoice",
                        ),
                      )}
                    />
                  </CardContent>
                </Card>
              )}

              {recentMovementsData.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Recent Movements</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DataTable
                      columns={recentMovementsTableColumns}
                      data={recentMovementsData}
                    />
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;
